import { Box, Card, Button, Text, Stack } from '@mantine/core'
import { IconBeach } from '@tabler/icons-react'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { APP_VERSION } from '@/version'

/**
 * Tela do colaborador em férias (spec 2026-07-24 §3.3).
 *
 * O backend recusa toda request autenticada com 403 `{code:'on_leave', until}`
 * enquanto o afastamento está ativo; o interceptor guarda a data e traz para
 * cá. Sem esta tela o usuário veria erros genéricos sem entender o motivo.
 *
 * O botão reinicia o login de propósito: o retorno é **automático** — no dia
 * seguinte à data o mesmo clique entra (o backend faz lazy-clear das férias).
 *
 * Segue o padrão canônico das telas de auth (SessionExpired.tsx): fundo
 * `--ai-auth-bg` com glow, BrandLogo, Card `--ai-auth-card-bg`.
 * Os tokens do arquetipo de auth ficam no `:root` do index.css.
 */
function formatUntil(raw: string | null): string | null {
  if (!raw) return null
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export function OnLeave() {
  const until = formatUntil(sessionStorage.getItem('on_leave_until'))

  const handleRetry = () => {
    window.location.replace(
      '/api/v1/auth/oidc/start?return_to=' + encodeURIComponent('/'),
    )
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'var(--ai-auth-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        style={{
          position: 'absolute',
          top: '-30%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '600px',
          background:
            'radial-gradient(circle, rgba(9, 141, 147, 0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Box style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        <Stack align="center" mb="xl" gap={4}>
          <BrandLogo variant="horizontal" color="white" width={180} />
          <Text
            size="xs"
            c="rgba(255,255,255,0.35)"
            mt={8}
            fw={600}
            tt="uppercase"
            style={{ letterSpacing: '0.08em' }}
          >
            Acesso suspenso
          </Text>
        </Stack>

        <Card
          radius="md"
          p="xl"
          style={{
            backgroundColor: 'var(--ai-auth-card-bg)',
            border: '1px solid var(--ai-auth-border)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.3)',
          }}
        >
          <Stack align="center" gap="sm">
            <Box
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(9, 141, 147, 0.1)',
                border: '1px solid rgba(9, 141, 147, 0.25)',
                boxShadow: '0 0 20px rgba(9, 141, 147, 0.15)',
              }}
            >
              <IconBeach size={30} color="#098d93" stroke={1.6} />
            </Box>
            <Text fw={700} size="lg" c="#e9ecef" ta="center">
              {until ? (
                <>
                  Você está de <span>férias até</span>{' '}
                  <Text span inherit c="#22b8cf">
                    {until}
                  </Text>
                </>
              ) : (
                'Você está de férias'
              )}
            </Text>
            <Text size="sm" c="#b6c2c9" ta="center" lh={1.5}>
              Durante as férias o acesso ao ConsoleAI fica suspenso. O retorno é{' '}
              <Text span inherit fw={700} c="#e9ecef">
                automático
              </Text>
              : a partir do dia seguinte você entra normalmente, com o mesmo
              usuário e senha.
            </Text>
            <Text size="xs" c="#868e96" ta="center" lh={1.5}>
              Precisa acessar antes disso? Fale com o gestor da sua Organização —
              ele pode encerrar as férias a qualquer momento.
            </Text>
          </Stack>

          <Button
            fullWidth
            mt="xl"
            size="md"
            radius="sm"
            onClick={handleRetry}
            style={{
              backgroundColor: 'var(--ai-brand-verde)',
              border: 'none',
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: '0.02em',
              transition: 'all var(--ai-transition-base)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#077a80'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--ai-brand-verde)'
            }}
          >
            Tentar entrar novamente
          </Button>

          <Text size="xs" c="#51585e" ta="center" mt="xl" fw={600}>
            ConsoleAI v{APP_VERSION}
          </Text>
        </Card>

        <Text size="xs" c="rgba(255,255,255,0.2)" ta="center" mt="md" fw={500}>
          &copy; 2025-2026 Amazonas Inovare. All rights reserved.
        </Text>
      </Box>
    </Box>
  )
}
